import 'keen-slider/keen-slider.min.css';

import { memo, useState } from 'react';

import { useKeenSlider } from 'keen-slider/react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CarouselButton, CarouselCard } from '@/shared/types/action';

const SLIDE_SPACING = 12;
export const TURN_ROW_SLIDE_SPACING = 8;
const SLIDER_STYLE = { overflow: 'visible' } as const;

const getButtonAction = (
  button: CarouselButton,
  onPostback?: (text: string) => void
): (() => void) | null => {
  switch (button.type) {
    case 'url':
      return button.url ? () => window.open(button.url, '_blank', 'noopener') : null;
    case 'phone':
      return button.value ? () => window.open(`tel:${button.value}`, '_blank', 'noopener') : null;
    case 'postback':
      return onPostback ? () => onPostback(button.value) : null;
    default:
      return null;
  }
};

export type CarouselNav = {
  sliderRef: ReturnType<typeof useKeenSlider<HTMLDivElement>>[0];
  currentSlide: number;
  canGoPrev: boolean;
  canGoNext: boolean;
  prev: () => void;
  next: () => void;
  moveTo: (index: number) => void;
};

/** Slider state, so a caller can drive the carousel from its own controls. */
export const useCarouselNav = (totalSlides: number, spacing = SLIDE_SPACING): CarouselNav => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    mode: 'snap',
    slides: { perView: 'auto', spacing },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
  });

  return {
    sliderRef,
    currentSlide,
    canGoPrev: currentSlide > 0,
    canGoNext: currentSlide < totalSlides - 1,
    prev: () => instanceRef.current?.prev(),
    next: () => instanceRef.current?.next(),
    moveTo: (index: number) => instanceRef.current?.moveToIdx(index),
  };
};

interface CarouselProps {
  cards: CarouselCard[];
  onPostback?: (text: string) => void;
  isReplay?: boolean;
  /** Supply the nav to drive the slider from outside; the built-in controls then go. */
  nav?: CarouselNav;
  /** Redesigned Try It card. Human chat and the dialog editor keep the default. */
  turnRow?: boolean;
}

const CarouselCardItem = memo(function CarouselCardItem({
  card,
  className,
  isActive,
  onPostback,
  isReplay,
  turnRow,
}: {
  card: CarouselCard;
  className?: string;
  isActive?: boolean;
  onPostback?: (text: string) => void;
  isReplay?: boolean;
  turnRow?: boolean;
}) {
  const { media, title, subtitle } = card;
  const hasMedia = Boolean(media?.url);
  // The backend sends buttons: null for cards without buttons, which a `= []` default would miss.
  const buttons = card.buttons ?? [];

  return (
    <div
      className={cn(
        'flex shrink-0 flex-col overflow-clip bg-background transition-opacity duration-200',
        turnRow
          ? 'min-h-60 max-w-60 grow-0 basis-60 items-start gap-2 rounded-xl border border-stroke pb-4 shadow-sm'
          : 'w-60 max-w-60 rounded-lg shadow-md',
        !isActive && 'opacity-30',
        className
      )}
    >
      {hasMedia && media ? (
        <div
          className={cn(
            turnRow ? 'h-24 shrink-0 self-stretch' : 'aspect-video min-h-0 w-full shrink-0'
          )}
        >
          {media.type === 'video' ? (
            <video src={media.url} controls className="size-full object-cover" />
          ) : (
            <img src={media.url} alt={title} className="size-full object-cover" />
          )}
        </div>
      ) : null}

      <div
        className={cn(
          turnRow
            ? // `basis-auto` + no shrink: the tallest card sets the height.
              'flex shrink-0 grow basis-auto flex-col items-start gap-4 self-stretch px-3'
            : cn(
                'flex min-h-0 grow flex-col gap-3 rounded-b-lg border border-stroke px-3 pt-2 pb-3',
                !hasMedia && 'rounded-t-lg'
              )
        )}
      >
        <div
          className={cn(
            turnRow
              ? 'flex shrink-0 flex-col items-start gap-1 self-stretch overflow-hidden'
              : 'flex min-h-0 grow flex-col gap-1 overflow-hidden'
          )}
        >
          <h4
            className={cn(
              'line-clamp-1 text-base leading-6 font-semibold text-foreground',
              turnRow && 'max-h-14 min-h-6 self-stretch overflow-hidden text-ellipsis'
            )}
          >
            {title}
          </h4>
          <p
            className={cn(
              'text-sm leading-5 text-foreground-muted',
              turnRow
                ? 'max-h-15 min-h-5 flex-1 self-stretch overflow-hidden text-ellipsis whitespace-nowrap'
                : 'line-clamp-2'
            )}
          >
            {subtitle}
          </p>
        </div>

        {buttons.length > 0 && (
          <div
            className={cn(
              'flex flex-col gap-2',
              turnRow ? 'mt-auto shrink-0 self-stretch' : 'shrink-0'
            )}
          >
            {buttons.map((button, i) => {
              const action = isReplay ? null : getButtonAction(button, onPostback);

              return (
                <button
                  key={`${i}_${button.type}`}
                  type="button"
                  disabled={action === null}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-center text-sm leading-6 font-medium',
                    turnRow
                      ? 'flex min-w-16 items-center justify-center gap-0 self-stretch border border-border bg-background text-foreground'
                      : 'w-full bg-secondary text-accent',
                    action && !turnRow && 'hover:bg-bg-muted',
                    action && turnRow && 'hover:bg-highlight-background',
                    !action && 'cursor-default opacity-60'
                  )}
                  onClick={action ?? undefined}
                >
                  {button.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export const Carousel = ({ cards, onPostback, isReplay, nav, turnRow }: CarouselProps) => {
  const ownNav = useCarouselNav(cards?.length ?? 0);
  const { sliderRef, currentSlide, canGoPrev, canGoNext, prev, next, moveTo } = nav ?? ownNav;

  if (!cards?.length) {
    return null;
  }

  const totalSlides = cards.length;

  return (
    <div className="flex w-full max-w-full min-w-0 flex-col gap-4">
      <div className={cn(totalSlides > 1 && '-ml-3 overflow-x-clip pl-3')}>
        <div
          ref={sliderRef}
          className={cn('keen-slider', turnRow && 'items-stretch self-stretch')}
          style={SLIDER_STYLE}
        >
          {cards.map((card, i) => (
            <CarouselCardItem
              key={`${card.title}-${card.media?.url ?? i}`}
              card={card}
              className="keen-slider__slide"
              isActive={i === currentSlide}
              onPostback={onPostback}
              isReplay={isReplay}
              turnRow={turnRow}
            />
          ))}
        </div>
      </div>

      {totalSlides > 1 && !nav && (
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            {cards.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => moveTo(i)}
                className={cn(
                  'size-3 rounded-full',
                  i === currentSlide ? 'bg-foreground-muted' : 'border border-stroke bg-background'
                )}
              />
            ))}
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline-neutral"
              size="icon-sm"
              className="size-8 rounded-full"
              disabled={!canGoPrev}
              onClick={prev}
            >
              <ArrowLeftIcon size={16} />
            </Button>
            <Button
              variant="outline-neutral"
              size="icon-sm"
              className="size-8 rounded-full"
              disabled={!canGoNext}
              onClick={next}
            >
              <ArrowRightIcon size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
